<template>
  <master-layout :show-footer="false">
    <div class="min-h-full bg-[#F6F5F1] px-5 py-6 md:px-8">
      <div class="mx-auto flex max-w-[520px] flex-col gap-5">
        <div>
          <p class="text-sm font-semibold uppercase tracking-[0.08em] text-[#6F6A60]">HOGC SSO</p>
          <h1 class="mt-2 text-[1.875rem] font-black leading-9 text-[#25221D]">Login approval</h1>
        </div>

        <div class="rounded-lg border border-[#E0DDD5] bg-white p-5 shadow-sm">
          <div v-if="loading" class="py-10 text-center text-[#5A554B]">Loading request...</div>

          <div v-else-if="errorMessage" class="flex flex-col gap-4">
            <h2 class="text-xl font-bold text-[#25221D]">Unable to load this request</h2>
            <p class="text-base leading-6 text-[#5A554B]">{{ errorMessage }}</p>
            <button
              type="button"
              class="rounded-full bg-[#3D3A35] px-5 py-3 font-semibold text-white"
              @click="fetchRequest"
            >
              Try Again
            </button>
          </div>

          <div v-else class="flex flex-col gap-5">
            <div>
              <p class="text-sm text-[#6F6A60]">Request from</p>
              <h2 class="mt-1 text-2xl font-bold text-[#25221D]">{{ request.clientName }}</h2>
            </div>

            <div class="rounded-md bg-[#F6F5F1] p-4">
              <div class="flex items-center justify-between gap-4">
                <span class="text-sm font-semibold text-[#6F6A60]">Status</span>
                <span class="text-sm font-bold capitalize text-[#25221D]">{{
                  request.status
                }}</span>
              </div>
              <div class="mt-3 flex items-center justify-between gap-4">
                <span class="text-sm font-semibold text-[#6F6A60]">Expires</span>
                <span class="text-sm text-[#25221D]">{{ expiryLabel }}</span>
              </div>
            </div>

            <!--
              Where the login was started. This is the member's only chance to
              notice a login that is not theirs, so it sits directly above the
              buttons rather than in the details block.
            -->
            <div v-if="hasInitiator" class="rounded-md border border-[#E0DDD5] p-4">
              <p class="text-sm font-semibold text-[#6F6A60]">Started from</p>
              <p class="mt-1 text-base text-[#25221D]">{{ initiatorSummary }}</p>
            </div>

            <div v-if="request.status === 'pending'" class="flex flex-col gap-4">
              <!--
                Same-device logins carry no code — the sister app is handed back
                this browser and sets its session there, so there is nothing for
                a code to protect. Asking for one that was never issued would
                make approval impossible.
              -->
              <!--
                The code is what binds this approval to the device that actually
                started the login. Without it, anyone could send this link to a
                member and have their approval hand over a session — being logged
                in is not proof that you started the login.
              -->
              <!--
                Typed entry is now only a fallback. A cross-device login is
                reached by SCANNING the QR, which carries the code in the link
                and prefills it — so the member just confirms. The input is
                revealed only if the code did not arrive by scan, or a scanned
                one was rejected.
              -->
              <div v-if="needsPairingCode && !codeFromScan">
                <label for="pairing-code" class="text-base font-semibold text-[#25221D]">
                  Enter the code shown on that screen
                </label>
                <p class="mt-1 text-sm leading-5 text-[#5A554B]">
                  If you are not looking at a screen with a code on it, someone else started this
                  login. Deny it.
                </p>
                <input
                  id="pairing-code"
                  v-model="pairingCode"
                  type="text"
                  inputmode="latin"
                  autocapitalize="characters"
                  autocomplete="one-time-code"
                  spellcheck="false"
                  maxlength="12"
                  placeholder="ABC123"
                  :disabled="submitting"
                  class="mt-3 w-full rounded-md border border-[#C9C4B8] px-4 py-3 text-center text-2xl font-bold uppercase tracking-[0.3em] text-[#25221D] disabled:opacity-50"
                  @keyup.enter="submitDecision('approve')"
                />
              </div>

              <p v-if="attemptsWarning" class="text-sm text-[#B3261E]">
                {{ attemptsWarning }}
              </p>

              <!--
                Outside the code block on purpose. Nested inside it, this never
                rendered for same-device approvals — which issue no code, so the
                block is absent — and a failed approval showed nothing at all:
                the screen reloaded and sat there with no explanation.
              -->
              <p v-if="decisionError" class="text-sm font-semibold text-[#B3261E]">
                {{ decisionError }}
              </p>

              <div class="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  :disabled="submitting"
                  class="rounded-full border border-[#3D3A35] px-5 py-3 font-semibold text-[#25221D] disabled:opacity-50"
                  @click="submitDecision('deny')"
                >
                  Deny
                </button>
                <button
                  type="button"
                  :disabled="submitting || (needsPairingCode && !pairingCode.trim())"
                  class="rounded-full bg-[#3D3A35] px-5 py-3 font-semibold text-white disabled:opacity-50"
                  @click="submitDecision('approve')"
                >
                  Approve
                </button>
              </div>
            </div>

            <div v-else class="flex flex-col gap-3">
              <p class="text-base font-semibold text-[#25221D]">{{ completionLabel }}</p>
              <!--
                Only offered same-device, where returning hands THIS browser back
                to the sister app. Cross-device has nothing to return to on the
                phone — the other screen completes on its own — so navigating
                there just lands on an error page.
              -->
              <button
                v-if="canReturnToRequester"
                type="button"
                class="rounded-full bg-[#3D3A35] px-5 py-3 font-semibold text-white"
                @click="returnToRequester"
              >
                Return to {{ requesterName }}
              </button>
              <button
                type="button"
                class="rounded-full border border-[#3D3A35] px-5 py-3 font-semibold text-[#25221D]"
                @click="closeApprovalScreen"
              >
                {{ closeLabel }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </master-layout>
</template>

<script>
import axios from 'axios';
import { AppLauncher } from '@capacitor/app-launcher';
import { Browser } from '@capacitor/browser';
import { mapState } from 'vuex';
import { isNativeNotWeb } from '@/utils/platform-check';

export default {
  name: 'SsoApprove',
  data() {
    return {
      loading: true,
      submitting: false,
      request: null,
      errorMessage: '',
      decisionError: '',
      now: Date.now(),
      timer: null,
      pairingCode: '',
      // True when the code arrived via a scanned QR (?code=). It is then
      // prefilled and the input stays hidden — the member just confirms.
      codeFromScan: false,
      // Held in memory only, never persisted: it is single-use and only valid
      // for the hand-back that follows immediately.
      returnToken: '',
    };
  },
  computed: {
    ...mapState('user', ['token']),
    requestId() {
      return this.$route.params.requestId;
    },
    authHeaders() {
      // The approval request must use the logged-in HOGC user's token. A deep link
      // only identifies the pending request; it does not authorize approval.
      const token = this.token || localStorage.getItem('token');
      return token ? { Authorization: `Bearer ${token}` } : {};
    },
    expiryLabel() {
      if (!this.request?.expiresAt) return '-';
      const expiresAt = new Date(this.request.expiresAt).getTime();
      const seconds = Math.max(0, Math.ceil((expiresAt - this.now) / 1000));
      if (this.request.status === 'expired' || seconds === 0) return 'Expired';
      if (seconds < 60) return `${seconds}s`;
      return `${Math.ceil(seconds / 60)}m`;
    },
    completionLabel() {
      if (!this.request) return '';
      if (this.request.status === 'approved') {
        // Cross-device: the login finishes on the screen the member scanned
        // from, so there is nothing to do on the phone but head back to it.
        if (this.needsPairingCode) {
          return 'Approved. Continue on the screen you scanned from.';
        }
        return 'Login request approved.';
      }
      if (this.request.status === 'denied') return 'Login request denied.';
      if (this.request.status === 'expired') return 'Login request expired.';
      return '';
    },
    /**
     * Whether to offer a "Return to the app" button. Only same-device: it needs
     * the one-time return token, and returning is meaningless cross-device where
     * the other screen completes on its own.
     */
    canReturnToRequester() {
      return (
        this.request?.status === 'approved' &&
        Boolean(this.returnToken) &&
        Boolean(this.request?.returnUrl)
      );
    },
    closeLabel() {
      return this.request?.status === 'approved' ? 'Done' : 'Close';
    },
    requesterName() {
      return this.request?.clientName?.replace(/_/g, ' ') || 'requester';
    },
    /**
     * Whether this request needs a pairing code.
     *
     * Cross-device logins do: the sister app fetches the assertion over a side
     * channel that anyone holding the request id could use, so approval has to
     * prove the approver is at the screen that started the login. Same-device
     * logins do not — the app hands that browser back and the session is set
     * there, so a forwarded link buys an attacker nothing.
     *
     * Defaults to true when the field is absent, so an older broker that does
     * not send it keeps the stricter behaviour instead of silently dropping it.
     */
    needsPairingCode() {
      return this.request?.requiresPairingCode !== false;
    },
    hasInitiator() {
      const initiator = this.request?.initiator;
      return Boolean(initiator && (initiator.device || initiator.location || initiator.ipHint));
    },
    initiatorSummary() {
      const { device, location, ipHint } = this.request?.initiator || {};
      // Read as a sentence: "Chrome on macOS · Singapore · 203.0.113.x". Any part
      // may be missing depending on what the sister app and the edge could see.
      return [device, location, ipHint].filter(Boolean).join(' · ');
    },
    attemptsWarning() {
      const left = this.request?.pairingAttemptsRemaining;
      // Only warn near the end. Counting down from five reads as a challenge.
      if (typeof left !== 'number' || left > 2) return '';
      if (left === 0) return 'Too many incorrect codes. Start the login again.';
      return `${left} attempt${left === 1 ? '' : 's'} left before this request is cancelled.`;
    },
  },
  async mounted() {
    this.timer = setInterval(() => {
      this.now = Date.now();
    }, 1000);
    // A scanned QR carries the code in the link, so the member never types it —
    // the camera reading it off the other screen is the proof of possession that
    // typing used to provide.
    const scanned = this.$route.query.code;
    if (typeof scanned === 'string' && scanned) {
      this.pairingCode = scanned;
      this.codeFromScan = true;
    }
    await this.fetchRequest();
  },
  beforeUnmount() {
    if (this.timer) clearInterval(this.timer);
  },
  methods: {
    async fetchRequest() {
      this.loading = true;
      this.errorMessage = '';
      this.decisionError = '';
      try {
        // Fetching details from the API lets the server enforce "is this request for
        // the current member?" before showing client name/status in the UI.
        const { data } = await axios.get(
          `${process.env.VUE_APP_API_URL}/v2/sso/requests/${this.requestId}`,
          { headers: this.authHeaders },
        );
        this.request = data;
      } catch (error) {
        this.errorMessage =
          error.response?.data?.message || 'This request is unavailable or no longer valid.';
      } finally {
        this.loading = false;
      }
    },
    async submitDecision(action) {
      if (this.submitting || !this.request) return;
      if (action === 'approve' && this.needsPairingCode && !this.pairingCode.trim()) return;
      this.submitting = true;
      this.decisionError = '';
      try {
        // Approve/deny is intentionally a server mutation, not local UI state. The
        // API records the decision and prevents approval after expiry or reuse.
        // The code is verified server-side; nothing here compares it, because the
        // screen never learns what the correct code is.
        const { data } = await axios.post(
          `${process.env.VUE_APP_API_URL}/v2/sso/requests/${this.requestId}/${action}`,
          action === 'approve' && this.needsPairingCode
            ? { pairingCode: this.pairingCode.trim() }
            : {},
          { headers: this.authHeaders },
        );
        this.request = data;
        if (data?.returnToken) this.returnToken = data.returnToken;

        // Same-device only: hand this browser back to the sister app. The
        // return token is issued for redirect-mode approvals and nothing else,
        // so its presence is the precise signal that returning is both possible
        // and correct. Cross-device (scanned) approvals have no token — the
        // screen the member scanned from completes by polling, and the phone
        // must NOT navigate to the return URL, which would just error here.
        if (action === 'approve' && this.request?.status === 'approved' && this.returnToken) {
          await this.returnToRequester();
          return;
        }
      } catch (error) {
        // Kept separate from errorMessage: that one replaces the whole card with
        // a "could not load" state, which would throw away the form the member is
        // standing in the middle of. A rejected code belongs next to the input.
        const message = error.response?.data?.message || `Unable to ${action} this request.`;
        // Re-read so the remaining-attempts count reflects the rejection, and
        // clear the box so a wrong code is not re-submitted by a second tap.
        // fetchRequest resets decisionError, so the message is set after it.
        if (action === 'approve') {
          this.pairingCode = '';
          // The scanned code did not work (usually a stale QR). Reveal the input
          // as a fallback rather than leaving a hidden, empty field that cannot
          // be retried — though re-scanning the latest code is the real fix.
          this.codeFromScan = false;
          await this.fetchRequest();
        }
        this.decisionError = message;
      } finally {
        this.submitting = false;
      }
    },
    async returnToRequester() {
      if (!this.request?.returnUrl) return;
      const url = new URL(this.request.returnUrl);
      url.searchParams.set('request_id', this.request.requestId);
      url.searchParams.set('status', this.request.status);
      // One-time token from the approve response. It is what proves to the
      // sister app that this completion came from the browser we are handing
      // back to, rather than from whoever else holds the request id. Same-device
      // logins cannot complete without it; cross-device ones do not issue one.
      if (this.returnToken) url.searchParams.set('return_token', this.returnToken);

      // Clear the approval screen before returning so switching back to HOGC One does
      // not leave the user stranded on a completed SSO request.
      await this.closeApprovalScreen();

      if (isNativeNotWeb()) {
        try {
          // Capacitor Browser opens SFSafariViewController on iOS, which looks like an
          // in-app browser. AppLauncher hands the URL to the OS so http/https returns
          // through Safari or the registered app handler instead.
          await AppLauncher.openUrl({ url: url.toString() });
        } catch (error) {
          console.error('[SSO] Unable to open requester with app launcher:', error?.message);
          // Fallback keeps the flow usable on platforms where AppLauncher cannot open
          // the local simulator URL.
          await Browser.open({ url: url.toString() });
        }
      } else {
        window.location.href = url.toString();
      }
    },
    async closeApprovalScreen() {
      // deepLinkPathObj is preserved through login/startup only until the SSO request
      // is completed or explicitly closed.
      localStorage.removeItem('deepLinkPathObj');
      if (this.$route.path !== '/home') {
        await this.$router.replace('/home');
      }
    },
  },
};
</script>
